import React, { useState, useEffect } from 'react';
import './recentActivities.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../utils/axiosInstance';
import { Spinner, Alert } from 'react-bootstrap';
import {
  formatISOToLocalTime,
  normalizeDbTimestampToUtcIso,
} from '../../../utils/timeUtils';

const RecentActivities = ({ adminIds, empRole }) => {
  const { t } = useTranslation();
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    if (adminIds?.length > 0) {
      fetchRecentActivities();
    }

    const detectTimezone = () => {
      const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedZone !== userTimeZone) {
        setUserTimeZone(detectedZone);
      }
    };

    window.addEventListener('focus', detectTimezone);
    return () => {
      window.removeEventListener('focus', detectTimezone);
    };
  }, [adminIds, userTimeZone]);

  const fetchRecentActivities = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const formattedDate = formatDate(today);
      const response = await axiosInstance.get(`/admin/employeesAttendance?date=${formattedDate}`);
      const data = response.data;

      // Filter activities based on role and adminIds
      let filteredActivities;
      if (empRole === 'SuperAdmin') {
        filteredActivities = data;
      } else {
        filteredActivities = data.filter(activity =>
          adminIds.includes(activity.emp_id)
        );
      }

      // Process the data to normalize timestamps
      const processedActivities = filteredActivities.map(entry => ({
        ...entry,
        punch_in_time: normalizeDbTimestampToUtcIso(entry.punch_in_time, entry.attendance_date),
        punch_out_time: normalizeDbTimestampToUtcIso(entry.punch_out_time, entry.attendance_date),
      }));

      // Sort by most recent activity
      processedActivities.sort((a, b) => {
        const aTime = a.punch_out_time || a.punch_in_time;
        const bTime = b.punch_out_time || b.punch_in_time;
        return new Date(bTime) - new Date(aTime);
      });

      setRecentActivities(processedActivities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  function getFirstName(fullName) {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  }

  const getActivityStatus = (activity) => {
    // Check for punch out time first to show the most recent status
    if (activity.punch_out_time) {
      return {
        status: 'logged out',
        time: formatISOToLocalTime(activity.punch_out_time),
        isOnline: false
      };
    }

    if (activity.punch_in_time) {
      return {
        status: 'logged in',
        time: formatISOToLocalTime(activity.punch_in_time),
        isOnline: true
      };
    }

    return {
      status: 'no activity',
      time: '-',
      isOnline: false
    };
  };

  // Format timezone for display
  const formatTimeZone = () => {
    try {
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: userTimeZone
      }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

      return timeZoneAbbr || userTimeZone.split('/').pop();
    } catch (e) {
      return userTimeZone.split('/').pop();
    }
  };

  return (
    <div className="recent-activities">
      {/* <div className="recent-activities-header">
        <span className="time-format">
          (HH:MM AM/PM {formatTimeZone()})
        </span>
      </div> */}
      {loading ? (
        <div className="recent-activities-Spinner">
          <Spinner animation="border" role="status">
            <span className=""></span>
          </Spinner>
          <span className="mt-2">{t('Please wait your data is loading')}</span>
        </div>
      ) : recentActivities.length === 0 ? (
        <Alert variant="light" className='recent-activities-Alert'>
          {t("dashboard_noRecentActivities")}
        </Alert>
      ) : (
        recentActivities.map((activity, index) => {
          const { status, time, isOnline } = getActivityStatus(activity);
          return (
            <div className="activity" key={`${activity.emp_id}-${index}`}>
              <div className={`status-dot ${isOnline ? 'online' : 'offline'}`}></div>
              <div className="activity-details">
                <span className="name">
                  <strong>{getFirstName(activity.p_name)}</strong> {status} at {time}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default RecentActivities;